/**
 * Insurer Portal - Registration Page
 * Multi-step registration with comprehensive verification for anti-scam protection
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Shield,
  Building2,
  Mail,
  Phone,
  Globe,
  FileText,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Users,
  Lock,
  MapPin,
  Upload,
  AlertTriangle,
  Info,
  FileCheck,
  BadgeCheck
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Company Info', icon: Building2 },
  { id: 2, title: 'Verification', icon: Shield },
  { id: 3, title: 'Products', icon: FileText },
  { id: 4, title: 'Contact', icon: Mail },
  { id: 5, title: 'Credentials', icon: Lock },
  { id: 6, title: 'Review', icon: CheckCircle2 }
];

const InsurerRegister = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Company Info
    legal_name: '',
    trading_name: '',
    country: 'SL',
    provider_type: 'insurance_company',
    years_in_business: '',
    description: '',
    
    // Step 2: Verification
    company_registration_number: '',
    licence_number: '',
    regulator_name: '',
    tax_id: '',
    website_url: '',
    official_email_domain: '',
    // Document URLs
    business_registration_doc_url: '',
    licence_doc_url: '',
    address_proof_doc_url: '',
    logo_url: '',
    
    // Step 3: Products
    policy_types_offered: ['comprehensive'],
    vehicle_types_covered: ['car'],
    usage_types_covered: ['private'],
    add_ons_offered: [],
    claims_sla_hours: 48,
    
    // Step 4: Contact
    business_address: '',
    city: '',
    postal_code: '',
    email: '',
    phone: '',
    claims_contact_email: '',
    
    // Step 5: Credentials
    password: '',
    confirm_password: ''
  });
  
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.legal_name) newErrors.legal_name = 'Legal name is required';
      if (!formData.country) newErrors.country = 'Country is required';
    }
    
    if (step === 2) {
      if (!formData.company_registration_number && !formData.licence_number) {
        newErrors.verification = 'Provide at least Registration Number OR Licence Number';
      }
    }
    
    if (step === 3) {
      if (formData.policy_types_offered.length === 0) {
        newErrors.policy_types = 'Select at least one policy type';
      }
    }
    
    if (step === 4) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.phone) newErrors.phone = 'Phone is required';
      if (!formData.business_address) newErrors.business_address = 'Business address is required';
    }
    
    if (step === 5) {
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Min 8 characters';
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleToggleArray = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleDocumentUpload = async (file, documentType) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload PDF, JPG, PNG, or GIF", variant: "destructive" });
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 5MB", variant: "destructive" });
      return;
    }
    
    try {
      setUploadingDoc(documentType);
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/insurance/insurer/upload-document?document_type=${documentType}&insurer_email=${formData.email || ''}`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        const docUrlField = `${documentType}_doc_url`;
        setFormData(prev => ({ ...prev, [docUrlField]: response.data.file_url }));
        toast({ title: "Document uploaded", description: `${documentType.replace('_', ' ')} uploaded successfully` });
      }
    } catch (error) {
      toast({ title: "Upload failed", description: error.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      toast({ title: "Please agree to terms", variant: "destructive" });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_URL}/api/insurance/insurer/register`, {
        legal_name: formData.legal_name,
        trading_name: formData.trading_name || null,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        country: formData.country,
        company_registration_number: formData.company_registration_number || null,
        licence_number: formData.licence_number || null,
        regulator_name: formData.regulator_name || null,
        tax_id: formData.tax_id || null,
        business_address: formData.business_address || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        website_url: formData.website_url || null,
        official_email_domain: formData.official_email_domain || null,
        years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
        provider_type: formData.provider_type,
        policy_types_offered: formData.policy_types_offered,
        vehicle_types_covered: formData.vehicle_types_covered,
        usage_types_covered: formData.usage_types_covered,
        add_ons_offered: formData.add_ons_offered,
        claims_contact_email: formData.claims_contact_email || null,
        claims_sla_hours: formData.claims_sla_hours,
        description: formData.description || null,
        // Document URLs
        business_registration_doc_url: formData.business_registration_doc_url || null,
        licence_doc_url: formData.licence_doc_url || null,
        address_proof_doc_url: formData.address_proof_doc_url || null,
        logo_url: formData.logo_url || null
      });
      
      if (response.data.success) {
        const score = response.data.verification_score || 0;
        const recommendation = response.data.recommendation || 'MANUAL_REVIEW';
        
        toast({
          title: "Registration Submitted!",
          description: `Verification Score: ${score}/100. ${recommendation === 'APPROVE' ? 'Fast-track approval likely!' : 'Our team will review within 24-48 hours.'}`
        });
        navigate('/insurer/login', { state: { registered: true } });
      }
      
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate verification score preview
  const getScorePreview = () => {
    let score = 0;
    
    if (formData.company_registration_number) score += 20;
    if (formData.licence_number) score += 20;
    if (formData.regulator_name) score += 10;
    if (formData.website_url) score += 10;
    
    // Document uploads bonus
    if (formData.business_registration_doc_url) score += 10;
    if (formData.licence_doc_url) score += 10;
    if (formData.address_proof_doc_url) score += 5;
    
    const emailDomain = formData.email.split('@')[1] || '';
    const freeEmails = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    if (emailDomain && !freeEmails.includes(emailDomain.toLowerCase())) score += 10;
    if (formData.phone) score += 5;
    if (formData.business_address) score += 5;
    
    const years = parseInt(formData.years_in_business) || 0;
    if (years >= 10) score += 10;
    else if (years >= 5) score += 7;
    else if (years >= 2) score += 4;
    
    return Math.min(score, 100);
  };

  const scorePreview = getScorePreview();
  const scoreColor = scorePreview >= 70 ? 'text-green-600' : scorePreview >= 40 ? 'text-yellow-600' : 'text-red-600';

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="legal_name">Legal Business Name *</Label>
        <Input
          id="legal_name"
          placeholder="Official registered company name"
          value={formData.legal_name}
          onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
          className={errors.legal_name ? 'border-red-500' : ''}
        />
        {errors.legal_name && <p className="text-xs text-red-500 mt-1">{errors.legal_name}</p>}
      </div>
      
      <div>
        <Label htmlFor="trading_name">Trading Name (if different)</Label>
        <Input
          id="trading_name"
          placeholder="Brand name used publicly"
          value={formData.trading_name}
          onChange={(e) => setFormData(prev => ({ ...prev, trading_name: e.target.value }))}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Country *</Label>
          <Select
            value={formData.country}
            onValueChange={(v) => setFormData(prev => ({ ...prev, country: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Provider Type *</Label>
          <Select
            value={formData.provider_type}
            onValueChange={(v) => setFormData(prev => ({ ...prev, provider_type: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="insurance_company">Insurance Company</SelectItem>
              <SelectItem value="broker">Insurance Broker</SelectItem>
              <SelectItem value="bancassurance">Bancassurance</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label>Years in Business</Label>
        <Select
          value={formData.years_in_business}
          onValueChange={(v) => setFormData(prev => ({ ...prev, years_in_business: v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Less than 1 year</SelectItem>
            <SelectItem value="2">1-3 years</SelectItem>
            <SelectItem value="5">3-5 years</SelectItem>
            <SelectItem value="10">5-10 years</SelectItem>
            <SelectItem value="15">10+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="description">Company Description</Label>
        <Textarea
          id="description"
          placeholder="Describe your insurance services..."
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>AI Verification:</strong> We verify all insurers to protect consumers. Accurate information speeds up approval.
        </AlertDescription>
      </Alert>
      
      {errors.verification && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{errors.verification}</AlertDescription>
        </Alert>
      )}
      
      <div>
        <Label>Company Registration Number <Badge variant="outline" className="ml-2 text-xs">+20 pts</Badge></Label>
        <Input
          placeholder="e.g., PV12345 (SL) or 12345678 (UK)"
          value={formData.company_registration_number}
          onChange={(e) => setFormData(prev => ({ ...prev, company_registration_number: e.target.value }))}
        />
      </div>
      
      {/* Business Registration Document Upload */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <Label className="flex items-center gap-2 mb-2">
          <FileCheck className="h-4 w-4 text-green-600" />
          Business Registration Certificate
          <Badge variant="outline" className="text-xs">+10 pts</Badge>
        </Label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="business_registration_doc"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleDocumentUpload(e.target.files[0], 'business_registration')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('business_registration_doc').click()}
            disabled={uploadingDoc === 'business_registration'}
          >
            {uploadingDoc === 'business_registration' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload Document</>
            )}
          </Button>
          {formData.business_registration_doc_url && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Uploaded
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (max 5MB)</p>
      </div>
      
      <div>
        <Label>Insurance Licence Number <Badge variant="outline" className="ml-2 text-xs">+20 pts</Badge></Label>
        <Input
          placeholder="IRCSL/FCA licence number"
          value={formData.licence_number}
          onChange={(e) => setFormData(prev => ({ ...prev, licence_number: e.target.value }))}
        />
      </div>
      
      {/* Licence Document Upload */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <Label className="flex items-center gap-2 mb-2">
          <FileCheck className="h-4 w-4 text-blue-600" />
          Insurance Licence Document
          <Badge variant="outline" className="text-xs">+10 pts</Badge>
        </Label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="licence_doc"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleDocumentUpload(e.target.files[0], 'licence')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('licence_doc').click()}
            disabled={uploadingDoc === 'licence'}
          >
            {uploadingDoc === 'licence' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload Document</>
            )}
          </Button>
          {formData.licence_doc_url && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Uploaded
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (max 5MB)</p>
      </div>
      
      <div>
        <Label>Regulator Name</Label>
        <Input
          placeholder={formData.country === 'UK' ? 'e.g., FCA' : 'e.g., IRCSL'}
          value={formData.regulator_name}
          onChange={(e) => setFormData(prev => ({ ...prev, regulator_name: e.target.value }))}
        />
      </div>
      
      <div>
        <Label>Tax ID / VAT Number</Label>
        <Input
          value={formData.tax_id}
          onChange={(e) => setFormData(prev => ({ ...prev, tax_id: e.target.value }))}
        />
      </div>
      
      <div>
        <Label>Company Website <Badge variant="outline" className="ml-2 text-xs">+10 pts</Badge></Label>
        <Input
          placeholder="https://www.yourcompany.com"
          value={formData.website_url}
          onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
        />
      </div>
      
      {/* Address Proof Document Upload */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <Label className="flex items-center gap-2 mb-2">
          <FileCheck className="h-4 w-4 text-purple-600" />
          Address Proof Document
          <Badge variant="outline" className="text-xs">Optional</Badge>
        </Label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="address_proof_doc"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleDocumentUpload(e.target.files[0], 'address_proof')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('address_proof_doc').click()}
            disabled={uploadingDoc === 'address_proof'}
          >
            {uploadingDoc === 'address_proof' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload Document</>
            )}
          </Button>
          {formData.address_proof_doc_url && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Uploaded
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">Utility bill, bank statement (max 5MB)</p>
      </div>
      
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Verification Score Preview</span>
          <span className={`text-2xl font-bold ${scoreColor}`}>{scorePreview}/100</span>
        </div>
        <Progress value={scorePreview} className="h-2" />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <Label className="mb-2 block">Policy Types Offered *</Label>
        <div className="flex flex-wrap gap-2">
          {['comprehensive', 'third_party', 'tpft'].map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleToggleArray('policy_types_offered', type)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                formData.policy_types_offered.includes(type)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              {type === 'comprehensive' ? 'Comprehensive' :
               type === 'third_party' ? 'Third Party Only' : 'TPFT'}
            </button>
          ))}
        </div>
        {errors.policy_types && <p className="text-xs text-red-500 mt-1">{errors.policy_types}</p>}
      </div>
      
      <div>
        <Label className="mb-2 block">Vehicle Types Covered</Label>
        <div className="flex flex-wrap gap-2">
          {['car', 'motorcycle', 'van', 'suv', 'three_wheeler'].map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleToggleArray('vehicle_types_covered', type)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                formData.vehicle_types_covered.includes(type)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              {type.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <Label className="mb-2 block">Usage Types Covered</Label>
        <div className="flex flex-wrap gap-2">
          {['private', 'business', 'taxi', 'hire_reward'].map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleToggleArray('usage_types_covered', type)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                formData.usage_types_covered.includes(type)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              {type.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <Label className="mb-2 block">Add-ons Available</Label>
        <div className="flex flex-wrap gap-2">
          {['windscreen', 'flood', 'theft', 'passenger', 'roadside', 'replacement_vehicle'].map(addon => (
            <button
              key={addon}
              type="button"
              onClick={() => handleToggleArray('add_ons_offered', addon)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                formData.add_ons_offered.includes(addon)
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
              }`}
            >
              {addon.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Claims SLA (hours)</Label>
        <Select
          value={String(formData.claims_sla_hours)}
          onValueChange={(v) => setFormData(prev => ({ ...prev, claims_sla_hours: parseInt(v) }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24">24 hours</SelectItem>
            <SelectItem value="48">48 hours</SelectItem>
            <SelectItem value="72">72 hours</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="business_address">Business Address *</Label>
        <Textarea
          id="business_address"
          placeholder="Full office address"
          value={formData.business_address}
          onChange={(e) => setFormData(prev => ({ ...prev, business_address: e.target.value }))}
          className={errors.business_address ? 'border-red-500' : ''}
          rows={2}
        />
        {errors.business_address && <p className="text-xs text-red-500 mt-1">{errors.business_address}</p>}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>City</Label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
          />
        </div>
        <div>
          <Label>Postal Code</Label>
          <Input
            value={formData.postal_code}
            onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="email">Business Email * <Badge variant="outline" className="ml-2 text-xs">Use company domain</Badge></Label>
        <Input
          id="email"
          type="email"
          placeholder="contact@yourcompany.com"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className={errors.email ? 'border-red-500' : ''}
        />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
      </div>
      
      <div>
        <Label htmlFor="phone">Phone Number *</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="+94 11 234 5678"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          className={errors.phone ? 'border-red-500' : ''}
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
      </div>
      
      <div>
        <Label>Claims Contact Email</Label>
        <Input
          type="email"
          placeholder="claims@yourcompany.com"
          value={formData.claims_contact_email}
          onChange={(e) => setFormData(prev => ({ ...prev, claims_contact_email: e.target.value }))}
        />
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <Alert className="bg-amber-50 border-amber-200">
        <Lock className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Create a strong password for your Insurer Portal access.
        </AlertDescription>
      </Alert>
      
      <div>
        <Label htmlFor="password">Password *</Label>
        <Input
          id="password"
          type="password"
          placeholder="Min 8 characters"
          value={formData.password}
          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
          className={errors.password ? 'border-red-500' : ''}
        />
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
      </div>
      
      <div>
        <Label htmlFor="confirm_password">Confirm Password *</Label>
        <Input
          id="confirm_password"
          type="password"
          value={formData.confirm_password}
          onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
          className={errors.confirm_password ? 'border-red-500' : ''}
        />
        {errors.confirm_password && <p className="text-xs text-red-500 mt-1">{errors.confirm_password}</p>}
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-indigo-800">Verification Score</span>
          <span className={`text-3xl font-bold ${scoreColor}`}>{scorePreview}/100</span>
        </div>
        <Progress value={scorePreview} className="h-3" />
        <p className="text-sm text-indigo-700 mt-2">
          {scorePreview >= 70 ? '✅ High score - faster approval' :
           scorePreview >= 40 ? '⚠️ May need manual review' :
           '❌ Please add more verification details'}
        </p>
      </div>
      
      <div className="space-y-3 text-sm">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Company</p>
          <p className="font-medium">{formData.legal_name}</p>
          {formData.trading_name && <p className="text-gray-600">Trading as: {formData.trading_name}</p>}
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Registration</p>
          <p>{formData.company_registration_number || 'Not provided'}</p>
          <p>{formData.licence_number || 'No licence'}</p>
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Products</p>
          <p>{formData.policy_types_offered.join(', ')}</p>
          <p>Vehicles: {formData.vehicle_types_covered.join(', ')}</p>
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Contact</p>
          <p>{formData.email}</p>
          <p>{formData.phone}</p>
        </div>
      </div>
      
      <div className="flex items-start gap-2 pt-4 border-t">
        <Checkbox
          id="terms"
          checked={agreedToTerms}
          onCheckedChange={setAgreedToTerms}
        />
        <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
          I confirm all information is accurate and agree to the{' '}
          <a href="#" className="text-indigo-600 hover:underline">Terms</a> and{' '}
          <a href="#" className="text-indigo-600 hover:underline">Privacy Policy</a>.
        </Label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-8 w-8 text-indigo-600" />
            <h1 className="text-2xl font-bold text-gray-900">Insurer Registration</h1>
          </div>
          <p className="text-gray-600">Join Zeilian&apos;s verified insurance provider network</p>
        </div>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center flex-1">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-all
                    ${isComplete ? 'bg-green-500 text-white' : 
                      isActive ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}
                  `}>
                    {isComplete ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-xs ${isActive ? 'text-indigo-600 font-semibold' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={(currentStep / STEPS.length) * 100} className="h-2" />
        </div>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
          </CardHeader>
          
          <CardContent>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
          </CardContent>
          
          <CardFooter className="flex justify-between border-t pt-4">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            
            {currentStep < 6 ? (
              <Button onClick={handleNext}>
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !agreedToTerms}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Submit Application</>
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
        
        <p className="text-center mt-6 text-gray-600">
          Already registered?{' '}
          <Link to="/insurer/login" className="text-indigo-600 hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default InsurerRegister;
